import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, 
  Save, 
  Download,
  Eye,
  Move,
  Type,
  Image,
  QrCode,
  Settings,
  RotateCcw,
  Palette,
  Plus,
  Upload,
  Trash2,
  LayoutList
} from 'lucide-react';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';
import { useToast } from '../components/Toast';
import QRCodeCanvas from 'qrcode';
import DraggableField from '../components/DraggableField';
import DynamicBadgeRenderer from '../components/DynamicBadgeRenderer';
import { PX_PER_PT, TEXT_CASES, TEXT_OVERFLOW, applyTextCase, fitFontSize, textOverflowMode } from '../utils/badgeText';
import { categoryOptions, hiddenCategoriesOf } from '../utils/badgeLayout';
import BadgeQuickLayoutDialog from '../components/BadgeQuickLayoutDialog';
import { useEventRoute } from '../utils/eventRef';

// Paper sizes in inches
const PAPER_SIZES = {
  'A6': { width: 4.13, height: 5.83, name: 'A6 (4.13" × 5.83")' },
  '4x6': { width: 4, height: 6, name: '4" × 6"' },
  '3.5x5': { width: 3.5, height: 5, name: '3.5" × 5"' },
  '3x4': { width: 3, height: 4, name: '3" × 4"' },
  '2.5x3.5': { width: 2.5, height: 3.5, name: '2.5" × 3.5"' },
  'custom': { width: 4, height: 6, name: 'Custom Size' }
};

// DPI for conversion (1 inch = 96 pixels at 96 DPI)
const DPI = 96;

// Dummy data for preview
const DUMMY_DATA = {
  name: 'John Doe',
  email: 'john.doe@example.com',
  company: 'Acme Corporation',
  designation: 'Software Engineer',
  purpose: 'Guest',
  redeemedBy: 'Admin User',
  redeemedAt: formatDateTime(new Date()),
  qrCodeBase64: null // Will be generated
};

// Available field types
const FIELD_TYPES = {
  text: { icon: Type, label: 'Text Field' },
  image: { icon: Image, label: 'Image Field' },
  qrCode: { icon: QrCode, label: 'QR Code' }
};

// Default field styles
const DEFAULT_FIELD_STYLE = {
  fontSize: 14,
  fontWeight: 'normal',
  color: '#000000',
  fontFamily: 'Arial, sans-serif',
  textAlign: 'left',
  rotation: 0 // Rotation in degrees
};

// Available font families
const FONT_FAMILIES = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Courier New, monospace', label: 'Courier New' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Lato, sans-serif', label: 'Lato' }
];

const BadgeDesignerPage = () => {
  const { eventId, eventRef } = useEventRoute();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const canvasRef = useRef(null);

  // State management
  const [selectedPaperSize, setSelectedPaperSize] = useState('4x6');
  const [customSize, setCustomSize] = useState({ width: 4, height: 6 });
  // Initialize with proper default badge config
  const [badgeConfig, setBadgeConfig] = useState(() => ({
    eventId,
    purpose: 'badge',
    paperSize: PAPER_SIZES['4x6'],
    fields: [],
    backgroundImage: null
  }));
  const [selectedField, setSelectedField] = useState(null);
  const [quickLayoutOpen, setQuickLayoutOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [dummyQR, setDummyQR] = useState('');
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [selectedPurpose, setSelectedPurpose] = useState('badge');
  const [isCreatingNew, setIsCreatingNew] = useState(true); // Start as creating new by default
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newTemplatePurpose, setNewTemplatePurpose] = useState('');

  // Fetch event data
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });

  // Fetch existing badge configuration for selected purpose
  const { data: existingConfig, isLoading: configLoading } = useQuery({
    queryKey: ['badgeDesignerConfig', eventId, selectedPurpose],
    queryFn: () => ApiService.getBadgeDesignerConfig(eventId, selectedPurpose),
    enabled: !!eventId && !!selectedPurpose,
    retry: false
  });

  // Fetch all existing badge configurations for this event
  const { data: allConfigs = [], isLoading: allConfigsLoading } = useQuery({
    queryKey: ['allBadgeConfigs', eventId],
    queryFn: () => ApiService.getAllBadgeConfigs(eventId),
    enabled: !!eventId,
    retry: false
  });

  // Manual field entry - no backend fetching needed

  // Save configuration mutation
  const saveConfigMutation = useMutation({
    mutationFn: (config) => ApiService.saveBadgeDesignerConfig(eventId, config.purpose, config),
    onSuccess: () => {
      queryClient.invalidateQueries(['badgeDesignerConfig', eventId, selectedPurpose]);
      queryClient.invalidateQueries(['allBadgeConfigs', eventId]);
      setIsCreatingNew(false);
      showSuccess('Badge configuration saved successfully!');
    },
    onError: (error) => {
      showError('Failed to save badge configuration: ' + error.message);
    }
  });

  // Generate dummy QR code on component mount
  useEffect(() => {
    const generateDummyQR = async () => {
      try {
        const qrDataURL = await QRCodeCanvas.toDataURL('https://example.com/sample-qr', {
          width: 200,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setDummyQR(qrDataURL);
      } catch (error) {
        console.error('Failed to generate dummy QR:', error);
      }
    };
    generateDummyQR();
  }, []);

  // Load existing configuration
  useEffect(() => {
    // Only run this if we're not manually creating a new template (isCreatingNew handles that)
    if (existingConfig && !isCreatingNew) {

      // Load existing template
      setBadgeConfig(existingConfig);
      setIsCreatingNew(false);
      if (existingConfig.backgroundImage) {
        setBackgroundImageUrl(existingConfig.backgroundImage);
      }
      if (existingConfig.paperSize) {
        const paperKey = Object.keys(PAPER_SIZES).find(key => 
          PAPER_SIZES[key].width === existingConfig.paperSize.width &&
          PAPER_SIZES[key].height === existingConfig.paperSize.height
        );
        if (paperKey) {
          setSelectedPaperSize(paperKey);
        } else {
          setSelectedPaperSize('custom');
          setCustomSize({
            width: existingConfig.paperSize.width,
            height: existingConfig.paperSize.height
          });
        }
      }
    }
  }, [existingConfig, isCreatingNew]);

  // Update badge config when purpose changes
  useEffect(() => {
    setBadgeConfig(prev => ({
      ...prev,
      purpose: selectedPurpose
    }));
  }, [selectedPurpose]);

  // No need to fetch available fields - manual entry only

  // Calculate canvas dimensions
  const getCanvasDimensions = () => {
    const paperSize = selectedPaperSize === 'custom' ? customSize : PAPER_SIZES[selectedPaperSize];
    const maxWidth = 600; // Maximum canvas width in pixels
    const aspectRatio = paperSize.height / paperSize.width;
    
    let width = maxWidth;
    let height = width * aspectRatio;
    
    // If height is too large, adjust based on height
    if (height > 800) {
      height = 800;
      width = height / aspectRatio;
    }
    
    return { width, height, aspectRatio };
  };

  // Convert inches to canvas pixels
  const inchesToCanvasPixels = (inches, canvasDimension, paperDimension) => {
    return (inches / paperDimension) * canvasDimension;
  };

  // Convert canvas pixels to inches
  const canvasPixelsToInches = (pixels, canvasDimension, paperDimension) => {
    return (pixels / canvasDimension) * paperDimension;
  };

  // Handle paper size change
  const handlePaperSizeChange = (newSize) => {
    setSelectedPaperSize(newSize);
    const paperSize = newSize === 'custom' ? customSize : PAPER_SIZES[newSize];
    setBadgeConfig(prev => ({
      ...prev,
      paperSize: {
        width: paperSize.width,
        height: paperSize.height
      }
    }));
  };

  // Update badge config when custom size changes
  useEffect(() => {
    if (selectedPaperSize === 'custom') {
      setBadgeConfig(prev => ({
        ...prev,
        paperSize: {
          width: customSize.width,
          height: customSize.height,
          name: `${customSize.width}" × ${customSize.height}"`
        }
      }));
    }
  }, [customSize, selectedPaperSize]);

  // Add new field to canvas using manual entry
  const addManualField = () => {
    if (!newFieldKey.trim() || !newFieldLabel.trim()) {
      showError('Field key and label are required');
      return;
    }

    // Check if field key already exists
    if (badgeConfig.fields.some(field => field.key === newFieldKey)) {
      showError('Field with this key already exists');
      return;
    }

    const newField = {
      id: `field_${Date.now()}`,
      key: newFieldKey.trim(),
      label: newFieldLabel.trim(),
      type: newFieldType,
      x: 0.5, // Default position in inches
      y: 0.5,
      width: newFieldType === 'image' || newFieldType === 'qrCode' ? 1 : 2,
      height: newFieldType === 'image' || newFieldType === 'qrCode' ? 1 : 0.5,
      visible: true,
      style: { ...DEFAULT_FIELD_STYLE }
    };

    setBadgeConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));

    // Reset form
    setNewFieldKey('');
    setNewFieldLabel('');
    setNewFieldType('text');
  };

  // Update field properties
  const updateField = (fieldId, updates) => {
    setBadgeConfig(prev => {
      const updatedFields = prev.fields.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      );
      
      // Also update selectedField if it's the one being updated
      if (selectedField && selectedField.id === fieldId) {
        const updatedField = updatedFields.find(f => f.id === fieldId);
        setSelectedField(updatedField);
      }
      
      return {
        ...prev,
        fields: updatedFields
      };
    });
  };

  // Handle background image URL input
  const handleBackgroundImageUrl = (url) => {
    const trimmedUrl = url.trim();
    
    if (trimmedUrl && !isValidImageUrl(trimmedUrl)) {
      showError('Please enter a valid image URL');
      return;
    }

    setBadgeConfig(prev => ({
      ...prev,
      backgroundImage: trimmedUrl || null
    }));
    setBackgroundImageUrl(trimmedUrl);
    
    if (trimmedUrl) {
      showSuccess('Background image URL set successfully');
    } else {
      showSuccess('Background image removed');
    }
  };

  // Validate image URL
  const isValidImageUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  };

  // Remove background image
  const removeBackgroundImage = () => {
    setBadgeConfig(prev => ({
      ...prev,
      backgroundImage: null
    }));
    setBackgroundImageUrl('');
    showSuccess('Background image removed');
  };

  // Handle creating a new template
  const handleCreateNewTemplate = () => {
    const trimmedPurpose = newTemplatePurpose.trim();

    
    if (!trimmedPurpose) {
      showError('Please enter a template name');
      return;
    }

    // Check if template already exists
    if (allConfigs.some(config => config.purpose === trimmedPurpose)) {
      showError(`Template "${trimmedPurpose}" already exists. Please choose a different name.`);
      return;
    }
    
    // Immediately set up blank template
    setBadgeConfig({
      eventId,
      purpose: trimmedPurpose,
      paperSize: PAPER_SIZES['4x6'],
      fields: [],
      backgroundImage: null
    });
    
    // Update states
    setSelectedPurpose(trimmedPurpose);
    setIsCreatingNew(true);
    setSelectedField(null);
    setBackgroundImageUrl('');
    setSelectedPaperSize('4x6');
    setCustomSize({ width: 4, height: 6 });
    
    // Close create modal
    setShowCreateNew(false);
    setNewTemplatePurpose('');
    
    showSuccess(`New template "${trimmedPurpose}" ready! Design your template and click Save.`);
  };

  // Handle selecting an existing template
  const handleSelectTemplate = (config) => {
    setSelectedPurpose(config.purpose);
    setIsCreatingNew(false);
    showSuccess(`Editing template: ${config.purpose}`);
  };

  // Remove field
  const removeField = (fieldId) => {
    setBadgeConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }));
    setSelectedField(null);
  };

  // Quick layout writes the same paperSize/fields shape as drag and drop; Save still sends it.
  const applyQuickLayout = ({ paperKey, customSize: size, paperSize: nextPaper, fields }) => {
    setSelectedPaperSize(paperKey);
    if (paperKey === 'custom') setCustomSize({ width: size.width, height: size.height });
    setBadgeConfig(prev => ({ ...prev, paperSize: nextPaper, fields }));
    setSelectedField(null);
    setQuickLayoutOpen(false);
    showSuccess('Layout applied. Drag to fine-tune, then Save.');
  };

  const quickLayoutSuggestions = [
    { key: 'name', label: 'Name' },
    { key: 'userId', label: 'User ID' },
    { key: 'qrCodeBase64', label: 'QR code' },
    ...(event?.formSchema || [])
      .map((field) => field?.label)
      .filter((label) => label && !['name', 'userid'].includes(String(label).toLowerCase()))
      .map((label) => ({ key: label, label })),
  ];

  // Save configuration - this triggers the POST API call
  const handleSave = () => {
    saveConfigMutation.mutate(badgeConfig);
  };

  // Get field value for preview - handles both dummy data and scan response data
  const getFieldValue = (field) => {
    // In preview mode, try to use dummy data first, then fall back to field label
    if (previewMode) {
      if (field.key === 'qrCodeBase64') {
        return dummyQR;
      }
      if (field.key in DUMMY_DATA) {
        return applyTextCase(DUMMY_DATA[field.key], field);
      }
    }
    
    // Default values based on field type
    if (field.type === 'qrCode') {
      return dummyQR;
    }
    if (field.type === 'image') {
      return 'https://via.placeholder.com/100x100.png?text=Photo';
    }
    
    // For text fields, show the field label as placeholder
    return applyTextCase(field.label, field);
  };

  if (eventLoading || configLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading badge designer...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-gray-600 mb-4">The requested event could not be found.</p>
          <Link to="/admin/events">
            <Button>Back to Events</Button>
          </Link>
        </div>
      </div>
    );
  }

  const canvasDims = getCanvasDimensions();
  const paperSize = selectedPaperSize === 'custom' ? customSize : PAPER_SIZES[selectedPaperSize];

  return (
    <div className="container mx-auto px-4 py-6" data-allow-number-keys>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Link to={`/admin/events/${eventRef}/registrations`}>
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Registrations
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">🪄 Dynamic Badge Designer</h1>
            <p className="text-gray-600">{event.name}</p>
          </div>
        </div>
        
        {/* Current Template Status */}
        {selectedPurpose && (
          <div className="px-3 py-2 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600">
              Template: <strong className="text-gray-900">{selectedPurpose}</strong>
              {isCreatingNew && <span className="text-blue-600 ml-2">(New)</span>}
              {!isCreatingNew && <span className="text-green-600 ml-2">(Existing)</span>}
            </p>
          </div>
        )}
        
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setQuickLayoutOpen(true)}>
            <LayoutList className="w-4 h-4 mr-2" />
            Quick layout
          </Button>
          <Button variant="outline" onClick={() => setPreviewMode(!previewMode)}>
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Design Mode' : 'Preview Mode'}
          </Button>
          <Button onClick={handleSave} disabled={saveConfigMutation.isLoading}>
            <Save className="w-4 h-4 mr-2" />
            {saveConfigMutation.isLoading 
              ? 'Saving...' 
              : isCreatingNew 
                ? 'Save Template' 
                : 'Update Template'
            }
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Download className="w-4 h-4 mr-2" />
            Print Badge
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Left Sidebar - Controls */}
        <div className="xl:col-span-1 space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {/* Template Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <QrCode className="w-5 h-5" />
                <span>Badge Templates</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {/* Create New Template */}
              <div>
                <Button 
                  onClick={() => setShowCreateNew(true)}
                  className="w-full"
                  variant="default"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Template
                </Button>
              </div>

              {/* Create New Template Modal */}
              {showCreateNew && (
                <div className="p-3 border border-blue-200 bg-blue-50 rounded-lg">
                  <Label>Template Name</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={newTemplatePurpose}
                      onChange={(e) => setNewTemplatePurpose(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newTemplatePurpose.trim()) {
                          handleCreateNewTemplate();
                        }
                      }}
                      placeholder="e.g., certificate, vip_pass, speaker_badge"
                      className="flex-1"
                      autoFocus
                    />
                    <Button
                      onClick={handleCreateNewTemplate}
                      size="sm"
                      disabled={!newTemplatePurpose.trim()}
                    >
                      Create
                    </Button>
                    <Button
                      onClick={() => {
                        setShowCreateNew(false);
                        setNewTemplatePurpose('');
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Existing Templates */}
              {allConfigs.length > 0 ? (
                <div>
                  <Label className="text-sm font-medium">Existing Templates</Label>
                  <div className="mt-2 space-y-2">
                    {allConfigs.map((config) => (
                      <div
                        key={config.purpose}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedPurpose === config.purpose
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleSelectTemplate(config)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">🎨</span>
                            <span className="font-medium">{config.purpose}</span>
                          </div>
                          {selectedPurpose === config.purpose && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {config.fields?.length || 0} fields • {config.paperSize?.name || 'Custom size'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <QrCode className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No templates created yet</p>
                  <p className="text-xs">Create your first badge template above</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Paper Size */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>Paper Size</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Size</Label>
                <Select value={selectedPaperSize} onValueChange={handlePaperSizeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAPER_SIZES).map(([key, size]) => (
                      <SelectItem key={key} value={key}>
                        {size.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedPaperSize === 'custom' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Width (inches)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={customSize.width}
                      onChange={(e) => setCustomSize(prev => ({
                        ...prev,
                        width: parseFloat(e.target.value) || 4
                      }))}
                    />
                  </div>
                  <div>
                    <Label>Height (inches)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={customSize.height}
                      onChange={(e) => setCustomSize(prev => ({
                        ...prev,
                        height: parseFloat(e.target.value) || 6
                      }))}
                    />
                  </div>
                </div>
              )}
              
              <div className="text-sm text-gray-600">
                Current: {paperSize.width}" × {paperSize.height}"
              </div>
            </CardContent>
          </Card>

          {/* Add New Field */}
          <Card>
            <CardHeader>
              <CardTitle>Add Field</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Field Key *</Label>
                <Input
                  value={newFieldKey}
                  onChange={(e) => setNewFieldKey(e.target.value)}
                  placeholder="e.g., name, email, company"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This should match the field name in QR scan response
                </p>
              </div>
              
              <div>
                <Label>Display Label *</Label>
                <Input
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                  placeholder="e.g., Full Name, Email Address"
                />
              </div>
              
              <div>
                <Label>Field Type</Label>
                <Select value={newFieldType} onValueChange={setNewFieldType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text Field</SelectItem>
                    <SelectItem value="image">Image Field</SelectItem>
                    <SelectItem value="qrCode">QR Code</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={addManualField}
                className="w-full"
                disabled={!newFieldKey.trim() || !newFieldLabel.trim()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Field to Badge
              </Button>
              
              <div className="text-xs text-gray-600 p-2 bg-blue-50 rounded">
                <strong>Common Field Keys:</strong><br />
                • name, email, phone<br />
                • company, designation<br />
                • qrCodeBase64 (for QR code)<br />
                • photo (for profile image)
              </div>
            </CardContent>
          </Card>

          {/* Background Image */}
          <Card>
            <CardHeader>
              <CardTitle>Background Image</CardTitle>
              <p className="text-sm text-gray-600">
                Add a background image URL for alignment during design (will not appear in final print)
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Background Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={backgroundImageUrl}
                    onChange={(e) => setBackgroundImageUrl(e.target.value)}
                    placeholder="https://example.com/background-image.jpg"
                    className="flex-1"
                  />
                  <Button 
                    onClick={() => handleBackgroundImageUrl(backgroundImageUrl)}
                    variant="outline"
                    size="sm"
                  >
                    Set
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter a direct URL to an image (PNG, JPG, etc.)
                </p>
              </div>
              
              {badgeConfig.backgroundImage && (
                <div className="space-y-3">
                  <div className="relative">
                    <img 
                      src={badgeConfig.backgroundImage} 
                      alt="Background Preview" 
                      className="w-full h-32 object-cover rounded-lg border"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x200.png?text=Image+Not+Found';
                        showError('Background image could not be loaded');
                      }}
                    />
                  </div>
                  <Button 
                    onClick={removeBackgroundImage}
                    variant="destructive" 
                    size="sm"
                    className="w-full"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Background Image
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Field Properties */}
          {selectedField && (
            <Card>
              <CardHeader>
                <CardTitle>Field Properties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Label</Label>
                  <Input
                    value={selectedField.label}
                    onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>X Position (in)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={selectedField.x}
                      onChange={(e) => updateField(selectedField.id, { x: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Y Position (in)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={selectedField.y}
                      onChange={(e) => updateField(selectedField.id, { y: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Width (in)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={selectedField.width}
                      onChange={(e) => updateField(selectedField.id, { width: parseFloat(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Label>Height (in)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={selectedField.height}
                      onChange={(e) => updateField(selectedField.id, { height: parseFloat(e.target.value) || 0.5 })}
                    />
                  </div>
                </div>

                {selectedField.type === 'text' && (
                  <>
                    <div>
                      <Label>Font Size (pt)</Label>
                      <Input
                        type="number"
                        value={selectedField.style?.fontSize || 14}
                        onChange={(e) => updateField(selectedField.id, {
                          style: { ...selectedField.style, fontSize: parseInt(e.target.value) || 14 }
                        })}
                      />
                    </div>
                    <div>
                      <Label>Font Weight</Label>
                      <Select
                        value={String(selectedField.style?.fontWeight || 'normal')}
                        onValueChange={(value) => updateField(selectedField.id, {
                          style: { ...selectedField.style, fontWeight: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="600">Semi-bold</SelectItem>
                          <SelectItem value="bold">Bold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Text Case</Label>
                      <Select
                        value={selectedField.style?.textCase || 'auto'}
                        onValueChange={(value) => updateField(selectedField.id, {
                          style: { ...selectedField.style, textCase: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TEXT_CASES.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Long text</Label>
                      <Select
                        value={selectedField.style?.overflow || 'wrap'}
                        onValueChange={(value) => updateField(selectedField.id, {
                          style: { ...selectedField.style, overflow: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TEXT_OVERFLOW.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Smallest font size (pt)</Label>
                      <Input
                        type="number"
                        min="4"
                        step="0.5"
                        placeholder={`${Math.round((selectedField.style?.fontSize || 14) * 6) / 10} (60%)`}
                        value={selectedField.style?.minFontSize ?? ''}
                        onChange={(e) => updateField(selectedField.id, {
                          style: { ...selectedField.style, minFontSize: e.target.value === '' ? null : Number(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <Label>Font Family</Label>
                      <Select
                        value={selectedField.style?.fontFamily || 'Arial, sans-serif'}
                        onValueChange={(value) => updateField(selectedField.id, {
                          style: { ...selectedField.style, fontFamily: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONT_FAMILIES.map(font => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Color</Label>
                      <Input
                        type="color"
                        value={selectedField.style?.color || '#000000'}
                        onChange={(e) => updateField(selectedField.id, {
                          style: { ...selectedField.style, color: e.target.value }
                        })}
                      />
                    </div>
                  </>
                )}

                {categoryOptions(event?.formSchema).length > 0 && (
                  <div>
                    <Label>Hide for categories</Label>
                    <div className="mt-1 space-y-1">
                      {categoryOptions(event?.formSchema).map((category) => {
                        const hidden = hiddenCategoriesOf(selectedField);
                        const checked = hidden.includes(category);
                        return (
                          <label key={category} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={checked}
                              onChange={() => updateField(selectedField.id, {
                                properties: {
                                  ...selectedField.properties,
                                  hiddenForCategories: checked
                                    ? hidden.filter((c) => c !== category)
                                    : [...hidden, category],
                                },
                              })}
                            />
                            {category}
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Fields below a hidden or empty field move up to close the gap.
                    </p>
                  </div>
                )}

                {/* Rotation control for all field types */}
                <div>
                  <Label className="flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Rotation (degrees)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="360"
                      step="15"
                      value={selectedField.style?.rotation || 0}
                      onChange={(e) => updateField(selectedField.id, {
                        style: { ...selectedField.style, rotation: parseInt(e.target.value) || 0 }
                      })}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const currentRotation = selectedField.style?.rotation || 0;
                        const newRotation = (currentRotation + 90) % 360;
                        updateField(selectedField.id, {
                          style: { ...selectedField.style, rotation: newRotation }
                        });
                      }}
                      title="Rotate 90°"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    0° = horizontal, 90° = vertical (rotated right)
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <Label>Visible</Label>
                  <Switch
                    checked={selectedField.visible}
                    onCheckedChange={(checked) => updateField(selectedField.id, { visible: checked })}
                  />
                </div>

                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => removeField(selectedField.id)}
                  className="w-full"
                >
                  Remove Field
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Canvas Area - Sticky on right */}
        <div className="xl:col-span-3">
          <div className="sticky top-4">
            <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Badge Canvas ({paperSize.width}" × {paperSize.height}")</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">
                    {previewMode ? 'Preview Mode' : 'Design Mode'}
                  </span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <div className="relative border-2 border-gray-300 border-dashed bg-white shadow-lg">
                <div
                  ref={canvasRef}
                  className="relative bg-white"
                  style={{
                    width: `${canvasDims.width}px`,
                    height: `${canvasDims.height}px`,
                    backgroundImage: badgeConfig.backgroundImage ? 
                      `url(${badgeConfig.backgroundImage})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Render badge fields */}
                  {badgeConfig.fields.map((field) => {
                    if (!field.visible) return null;

                    const fieldValue = getFieldValue(field);

                    return (
                      <DraggableField
                        key={field.id}
                        field={field}
                        paperSize={paperSize}
                        canvasDimensions={canvasDims}
                        isSelected={selectedField?.id === field.id}
                        onSelect={setSelectedField}
                        onUpdate={updateField}
                        onRemove={removeField}
                      >
                        {field.type === 'text' && (
                          <div
                            className="w-full h-full flex items-center justify-center"
                            style={{
                              fontSize: `${fitFontSize(fieldValue, field) * PX_PER_PT * (canvasDims.width / (paperSize.width * DPI))}px`,
                              color: field.style?.color || '#000000',
                              fontWeight: field.style?.fontWeight || 'normal',
                              textAlign: 'center',
                              lineHeight: 1.2,
                              ...(textOverflowMode(field) === 'shrink'
                                ? { whiteSpace: 'nowrap', overflow: 'hidden' }
                                : { whiteSpace: 'normal', overflowWrap: 'anywhere', overflow: 'visible' }),
                            }}
                          >
                            <span style={{ maxWidth: '100%', minWidth: 0 }}>{fieldValue}</span>
                          </div>
                        )}
                        
                        {field.type === 'image' && (
                          <img
                            src={fieldValue}
                            alt={field.label}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/100x100.png?text=Photo';
                            }}
                          />
                        )}
                        
                        {field.type === 'qrCode' && fieldValue && (
                          <img
                            src={fieldValue}
                            alt="QR Code"
                            className="w-full h-full object-contain"
                          />
                        )}
                      </DraggableField>
                    );
                  })}

                  {/* Grid lines for design mode */}
                  {!previewMode && (
                    <svg
                      className="absolute inset-0 pointer-events-none opacity-20"
                      width={canvasDims.width}
                      height={canvasDims.height}
                    >
                      {/* Vertical grid lines every 0.5 inches */}
                      {Array.from({ length: Math.floor(paperSize.width * 2) + 1 }, (_, i) => {
                        const x = inchesToCanvasPixels(i * 0.5, canvasDims.width, paperSize.width);
                        return (
                          <line
                            key={`v-${i}`}
                            x1={x}
                            y1={0}
                            x2={x}
                            y2={canvasDims.height}
                            stroke="#e5e7eb"
                            strokeWidth={1}
                          />
                        );
                      })}
                      
                      {/* Horizontal grid lines every 0.5 inches */}
                      {Array.from({ length: Math.floor(paperSize.height * 2) + 1 }, (_, i) => {
                        const y = inchesToCanvasPixels(i * 0.5, canvasDims.height, paperSize.height);
                        return (
                          <line
                            key={`h-${i}`}
                            x1={0}
                            y1={y}
                            x2={canvasDims.width}
                            y2={y}
                            stroke="#e5e7eb"
                            strokeWidth={1}
                          />
                        );
                      })}
                    </svg>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>

      <BadgeQuickLayoutDialog
        open={quickLayoutOpen}
        onClose={() => setQuickLayoutOpen(false)}
        paperSizes={PAPER_SIZES}
        paperKey={selectedPaperSize}
        customSize={customSize}
        fields={badgeConfig.fields}
        backgroundImage={badgeConfig.backgroundImage || backgroundImageUrl}
        fieldSuggestions={quickLayoutSuggestions}
        dummyQR={dummyQR}
        onApply={applyQuickLayout}
      />
    </div>
  );
};

export default BadgeDesignerPage;
